import { collection, addDoc, updateDoc, doc, getDocs, deleteDoc, query, where, getDoc, writeBatch, orderBy, limit, startAfter, QueryDocumentSnapshot, DocumentData, arrayUnion, runTransaction } from "firebase/firestore";
import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { Lead, HistoryEntry, NotesEntry } from "@/components/lead-card";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { cache } from "@/lib/cache";

import { applyNoteMutation, getLeadAppends, getLeadNotes, type NoteMutation } from "@/lib/lead-update";

const COLLECTION_NAME = "leads";
export const LEADS_COLLECTION = COLLECTION_NAME;

// Helper function to check if a date is valid
const isValidDate = (date: any): boolean => {
  if (!date) return false;

  try {
    const d = new Date(date);
    return !isNaN(d.getTime());
  } catch (error) {
    return false;
  }
};

// Helper function to convert Firestore timestamp fields to Date objects
const convertFirestoreData = (doc: any): Lead => {
  const data = doc.data();

  return {
    id: doc.id,
    firstName: data.firstName || "",
    lastName: data.lastName || "",
    position: data.position || "",
    email: data.email || "",
    phone: data.phone || "",
    status: data.status || "new",
    priority: data.priority || "medium",
    nextContactDate: isValidDate(data.nextContactDate) ? new Date(data.nextContactDate) : data.nextContactDate || "",
    lastContactDate: isValidDate(data.lastContactDate) ? new Date(data.lastContactDate) : data.lastContactDate || "",
    notes: data.notes || "",
    notesHistory: data.notesHistory || [],
    assignedTo: data.assignedTo || "",
    attachment: data.attachment || "",
    history: data.history || [],
    marketplacePurchaseInfo: data.marketplacePurchaseInfo || "",
    dataProtectionConsent: data.dataProtectionConsent || false,
    tenantId: data.tenantId || LEGACY_TENANT_ID,
    folderId: data.folderId || null,
    createdAt: data.createdAt || undefined,
    lastUpdated: data.lastUpdated || undefined,
    source: data.source || undefined,
    marketLeadId: data.marketLeadId || undefined,
    company: data.company || undefined,
    profession: data.profession || undefined,
  };
};

// Helper function to prepare data for Firestore (handle Date objects)
const prepareForFirestore = (data: any) => {
  console.log("Preparing data for Firestore:", data);

  // Create a copy to avoid mutating the original
  const firestoreData = { ...data };

  // Initialize date fields with empty strings if they're undefined
  // This will ensure Firestore doesn't error on undefined values
  if (firestoreData.nextContactDate === undefined) {
    firestoreData.nextContactDate = "";
  } else if (firestoreData.nextContactDate instanceof Date) {
    // Handle valid Date objects
    if (!isNaN(firestoreData.nextContactDate.getTime())) {
      firestoreData.nextContactDate = firestoreData.nextContactDate.toISOString();
    } else {
      firestoreData.nextContactDate = "";
    }
  } else if (typeof firestoreData.nextContactDate === 'string' && firestoreData.nextContactDate) {
    // Keep valid string dates as they are
    try {
      const dateObj = new Date(firestoreData.nextContactDate);
      if (isNaN(dateObj.getTime())) {
        firestoreData.nextContactDate = "";
      }
    } catch (error) {
      firestoreData.nextContactDate = "";
    }
  } else if (firestoreData.nextContactDate && typeof firestoreData.nextContactDate === 'object') {
    // Handle object format that might come from Firestore
    if (firestoreData.nextContactDate.__time__) {
      firestoreData.nextContactDate = firestoreData.nextContactDate.__time__;
    } else {
      try {
        // Try to convert to ISO string if it's a valid date-like object
        const dateObj = new Date(firestoreData.nextContactDate);
        if (!isNaN(dateObj.getTime())) {
          firestoreData.nextContactDate = dateObj.toISOString();
        } else {
          firestoreData.nextContactDate = "";
        }
      } catch (error) {
        firestoreData.nextContactDate = "";
      }
    }
  }

  // Apply the same logic to lastContactDate
  if (firestoreData.lastContactDate === undefined) {
    firestoreData.lastContactDate = "";
  } else if (firestoreData.lastContactDate instanceof Date) {
    if (!isNaN(firestoreData.lastContactDate.getTime())) {
      firestoreData.lastContactDate = firestoreData.lastContactDate.toISOString();
    } else {
      firestoreData.lastContactDate = "";
    }
  } else if (typeof firestoreData.lastContactDate === 'string' && firestoreData.lastContactDate) {
    try {
      const dateObj = new Date(firestoreData.lastContactDate);
      if (isNaN(dateObj.getTime())) {
        firestoreData.lastContactDate = "";
      }
    } catch (error) {
      firestoreData.lastContactDate = "";
    }
  } else if (firestoreData.lastContactDate && typeof firestoreData.lastContactDate === 'object') {
    if (firestoreData.lastContactDate.__time__) {
      firestoreData.lastContactDate = firestoreData.lastContactDate.__time__;
    } else {
      try {
        const dateObj = new Date(firestoreData.lastContactDate);
        if (!isNaN(dateObj.getTime())) {
          firestoreData.lastContactDate = dateObj.toISOString();
        } else {
          firestoreData.lastContactDate = "";
        }
      }
      catch (error) {
        firestoreData.lastContactDate = "";
      }
    }
  }

  // Handle position field consistently
  if (firestoreData.position === undefined) {
    firestoreData.position = "";
  }

  // Ensure history is an array
  if (!Array.isArray(firestoreData.history)) {
    firestoreData.history = [];
  }

  // Process history entries to ensure dates are in ISO string format
  if (firestoreData.history && firestoreData.history.length > 0) {
    firestoreData.history = firestoreData.history.map((entry: any) => {
      // Ensure date is in ISO string format
      if (entry.date && entry.date instanceof Date) {
        return {
          ...entry,
          date: entry.date.toISOString()
        };
      }
      return entry;
    });
  }

  // Double-check that there are no undefined values left
  Object.keys(firestoreData).forEach(key => {
    if (firestoreData[key] === undefined) {
      delete firestoreData[key];
    }
  });

  // Ensure the marketplace purchase info field is properly prepared
  if (firestoreData.marketplacePurchaseInfo === undefined) {
    firestoreData.marketplacePurchaseInfo = "";
  }

  logger.log("Prepared data for Firestore:", firestoreData);
  return firestoreData;
};

export const fetchLeads = async (tenantId: string = LEGACY_TENANT_ID): Promise<Lead[]> => {
  try {
    const leadsRef = collection(db, COLLECTION_NAME);

    // Always use server-side WHERE clause filtering
    // This requires all documents to have a tenantId field
    // Run the migration script (scripts/migrate-tenant-ids.ts) to add tenantId to existing documents
    const q = query(leadsRef, where("tenantId", "==", tenantId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => convertFirestoreData(doc));
  } catch (error) {
    console.error("Error fetching leads:", error);
    return [];
  }
};

export const fetchUserLeads = async (userId: string, tenantId: string = LEGACY_TENANT_ID): Promise<Lead[]> => {
  try {
    const leadsRef = collection(db, COLLECTION_NAME);

    // Use composite WHERE clause for both assignedTo and tenantId
    // This requires a composite index: [tenantId, assignedTo]
    // Deploy indexes with: firebase deploy --only firestore:indexes
    const q = query(
      leadsRef,
      where("tenantId", "==", tenantId),
      where("assignedTo", "==", userId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertFirestoreData(doc));
  } catch (error) {
    console.error("Error fetching user leads:", error);
    return [];
  }
};

/**
 * Fetches leads based on the current user's role and tenant.
 * - Admin/Developer: Returns all leads within their tenant
 * - Berater/User: Returns only leads assigned to them within their tenant
 *
 * Results are cached for 5 minutes by default. Use skipCache=true to force a fresh fetch.
 */
export const fetchLeadsForCurrentUser = async (
  userId: string,
  userRole: string,
  tenantId: string = LEGACY_TENANT_ID,
  skipCache: boolean = false
): Promise<Lead[]> => {
  try {
    const cacheKey = `leads:${userId}:${userRole}:${tenantId}`;

    // Check cache first if not skipped
    if (!skipCache) {
      const cached = cache.get<Lead[]>(cacheKey);
      if (cached) {
        logger.log(`[Cache HIT] leads for user ${userId}`);
        return cached;
      }
    }

    logger.log(`[Cache MISS] Fetching leads for user ${userId}`);

    let result: Lead[];

    // Admin and Developer can see all leads in their tenant
    if (userRole === 'admin' || userRole === 'developer') {
      result = await fetchLeads(tenantId);
    } else {
      // Berater and regular users can only see their assigned leads
      result = await fetchUserLeads(userId, tenantId);
    }

    // Store in cache
    cache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error("Error fetching leads for current user:", error);
    return [];
  }
};

// ============================================
// PAGINATION FUNCTIONS - For better performance
// ============================================

export interface PaginatedLeadsResult {
  leads: Lead[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  totalLoaded: number;
}

/**
 * Fetches leads with pagination for Admin/Developer (all tenant leads)
 * Uses cursor-based pagination for optimal Firestore performance
 */
export const fetchLeadsPaginated = async (
  tenantId: string = LEGACY_TENANT_ID,
  pageSize: number = 100,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null
): Promise<PaginatedLeadsResult> => {
  try {
    const leadsRef = collection(db, COLLECTION_NAME);

    let q = query(
      leadsRef,
      where("tenantId", "==", tenantId),
      orderBy("lastUpdated", "desc"),
      limit(pageSize)
    );

    if (lastDoc) {
      q = query(
        leadsRef,
        where("tenantId", "==", tenantId),
        orderBy("lastUpdated", "desc"),
        startAfter(lastDoc),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    const leads = querySnapshot.docs.map(doc => convertFirestoreData(doc));
    const newLastDoc = querySnapshot.docs.length > 0
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;

    return {
      leads,
      lastDoc: newLastDoc,
      hasMore: querySnapshot.docs.length === pageSize,
      totalLoaded: leads.length
    };
  } catch (error) {
    console.error("Error fetching paginated leads:", error);
    return { leads: [], lastDoc: null, hasMore: false, totalLoaded: 0 };
  }
};

/**
 * Fetches user's leads with pagination (for Berater/regular users)
 */
export const fetchUserLeadsPaginated = async (
  userId: string,
  tenantId: string = LEGACY_TENANT_ID,
  pageSize: number = 100,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null
): Promise<PaginatedLeadsResult> => {
  try {
    const leadsRef = collection(db, COLLECTION_NAME);

    let q = query(
      leadsRef,
      where("tenantId", "==", tenantId),
      where("assignedTo", "==", userId),
      orderBy("lastUpdated", "desc"),
      limit(pageSize)
    );

    if (lastDoc) {
      q = query(
        leadsRef,
        where("tenantId", "==", tenantId),
        where("assignedTo", "==", userId),
        orderBy("lastUpdated", "desc"),
        startAfter(lastDoc),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    const leads = querySnapshot.docs.map(doc => convertFirestoreData(doc));
    const newLastDoc = querySnapshot.docs.length > 0
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;

    return {
      leads,
      lastDoc: newLastDoc,
      hasMore: querySnapshot.docs.length === pageSize,
      totalLoaded: leads.length
    };
  } catch (error) {
    console.error("Error fetching paginated user leads:", error);
    return { leads: [], lastDoc: null, hasMore: false, totalLoaded: 0 };
  }
};

/**
 * Fetches leads with pagination based on user role
 * - Admin/Developer viewing "all": All tenant leads
 * - Admin/Developer viewing specific user: Only that user's leads
 * - Berater/User: Only their assigned leads
 *
 * @param targetUserId - Optional: If admin selects a specific user, load only their leads
 */
export const fetchLeadsForCurrentUserPaginated = async (
  userId: string,
  userRole: string,
  tenantId: string = LEGACY_TENANT_ID,
  pageSize: number = 100,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
  targetUserId?: string | null // NEW: For admin viewing specific user's leads
): Promise<PaginatedLeadsResult> => {
  try {
    if (userRole === 'admin' || userRole === 'developer') {
      // If admin selected a specific user (not "all"), load only that user's leads
      if (targetUserId && targetUserId !== 'all') {
        return await fetchUserLeadsPaginated(targetUserId, tenantId, pageSize, lastDoc);
      }
      // Otherwise load all tenant leads
      return await fetchLeadsPaginated(tenantId, pageSize, lastDoc);
    }
    // Regular users always see only their own leads
    return await fetchUserLeadsPaginated(userId, tenantId, pageSize, lastDoc);
  } catch (error) {
    console.error("Error fetching paginated leads for current user:", error);
    return { leads: [], lastDoc: null, hasMore: false, totalLoaded: 0 };
  }
};

export const createLead = async (lead: Omit<Lead, "id">): Promise<Lead | null> => {
  try {
    const nowIso = new Date().toISOString();

    // Initialize date fields with empty strings if not provided.
    // createdAt and lastUpdated are mandatory for time-based KPIs — set them
    // centrally so every creation path (UI form, CSV import, marketplace,
    // direct API) produces leads with valid timestamps.
    const leadWithInitializedDates = {
      ...lead,
      nextContactDate: lead.nextContactDate || "",
      lastContactDate: lead.lastContactDate || "",
      marketplacePurchaseInfo: lead.marketplacePurchaseInfo || "",
      dataProtectionConsent: lead.dataProtectionConsent || false,
      tenantId: lead.tenantId || LEGACY_TENANT_ID,
      createdAt: lead.createdAt || nowIso,
      lastUpdated: lead.lastUpdated || nowIso,
    };

    // Add creation history event
    const historyEntry: HistoryEntry = {
      date: new Date().toISOString(),
      type: "created" as const,
      changes: [],
      user: leadWithInitializedDates.assignedTo || "System"
    };

    const leadWithHistory = {
      ...leadWithInitializedDates,
      history: [historyEntry]
    };

    const firestoreData = prepareForFirestore(leadWithHistory);

    logger.log("Creating lead with data:", firestoreData);

    const docRef = await addDoc(collection(db, COLLECTION_NAME), firestoreData);

    // Invalidate cache for this tenant since new lead was created
    cache.invalidatePattern('^leads:');

    // Return the created lead with its new ID
    return {
      id: docRef.id,
      ...leadWithHistory
    } as Lead;
  } catch (error) {
    logger.error("Error creating lead:", error);
    toast({
      title: "Fehler beim Erstellen des Leads",
      description: "Der Lead konnte nicht erstellt werden.",
      variant: "destructive",
    });
    return null;
  }
};

export async function updateLead(leadId: string, updateData: Partial<Lead>, originalLead: Lead): Promise<boolean> {
  try {
    const leadRef = doc(db, COLLECTION_NAME, leadId);
    const leadDoc = await getDoc(leadRef);

    if (!leadDoc.exists()) {
      logger.error("Lead not found:", leadId);
      return false;
    }

    // Create a new object with only defined values
    const finalUpdateData: Record<string, any> = {
      lastUpdated: new Date().toISOString()
    };

    // Add all defined values from updateData, excluding the id field
    Object.entries(updateData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== 'id') {
        finalUpdateData[key] = value;
      }
    });

    // Helper to convert date fields to ISO strings
    const convertDateField = (dateValue: any): string => {
      if (!dateValue) return "";
      if (typeof dateValue === 'string') {
        // Validate the string is a valid date
        try {
          const d = new Date(dateValue);
          return isNaN(d.getTime()) ? "" : dateValue;
        } catch {
          return "";
        }
      }
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? "" : dateValue.toISOString();
      }
      if (typeof dateValue === 'object') {
        // Handle Firestore Timestamp with toDate()
        if (typeof dateValue.toDate === 'function') {
          try {
            return dateValue.toDate().toISOString();
          } catch {
            return "";
          }
        }
        // Handle __time__ format
        if (dateValue.__time__) {
          return dateValue.__time__;
        }
        // Handle seconds format
        if (dateValue.seconds !== undefined) {
          try {
            return new Date(dateValue.seconds * 1000).toISOString();
          } catch {
            return "";
          }
        }
        // Empty object {} or unknown format
        return "";
      }
      return "";
    };

    // Convert date fields to ISO strings
    if ('nextContactDate' in finalUpdateData) {
      finalUpdateData.nextContactDate = convertDateField(finalUpdateData.nextContactDate);
    }
    if ('lastContactDate' in finalUpdateData) {
      finalUpdateData.lastContactDate = convertDateField(finalUpdateData.lastContactDate);
    }

    // Deep clean function to remove all undefined/null values, even in nested objects/arrays
    function deepClean(obj: any): any {
      if (obj === null || obj === undefined) {
        return null;
      }

      if (Array.isArray(obj)) {
        return obj
          .filter(item => item !== undefined && item !== null)
          .map(item => deepClean(item));
      }

      if (typeof obj === 'object') {
        const cleaned: any = {};
        Object.entries(obj).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            const cleanedValue = deepClean(value);
            if (cleanedValue !== null && cleanedValue !== undefined) {
              cleaned[key] = cleanedValue;
            }
          }
        });
        return cleaned;
      }

      return obj;
    }

    // Log data before deep cleaning
    logger.log("Before deepClean:", JSON.stringify(finalUpdateData, null, 2));

    // Apply deep cleaning
    const cleanedData = deepClean(finalUpdateData);

    // Log data after deep cleaning
    logger.log("After deepClean:", JSON.stringify(cleanedData, null, 2));

    // Check for any undefined values that might have slipped through
    const hasUndefined = JSON.stringify(cleanedData).includes('undefined');
    if (hasUndefined) {
      logger.error("WARNING: Found undefined in cleaned data!");
    }

    const appends = getLeadAppends(updateData, originalLead);
    delete cleanedData.notesHistory;
    delete cleanedData.history;
    if (appends.notes.length) cleanedData.notesHistory = arrayUnion(...deepClean(appends.notes));
    if (appends.history.length) cleanedData.history = arrayUnion(...deepClean(appends.history));

    if (appends.notes.length) {
      await runTransaction(db, async transaction => {
        const latest = await transaction.get(leadRef);
        if (!latest.exists()) throw new Error('Lead not found');
        const notes = getLeadNotes({ ...latest.data(), id: leadId });
        transaction.update(leadRef, {
          ...cleanedData,
          notes: '',
          notesHistory: [
            ...deepClean(appends.notes).filter((note: NotesEntry) => !notes.some(existing => existing.id === note.id)),
            ...notes,
          ],
        });
      });
    } else {
      await updateDoc(leadRef, cleanedData);
    }

    // Invalidate cache for this tenant since lead was updated
    cache.invalidatePattern('^leads:');

    return true;
  } catch (error) {
    logger.error("Error updating lead:", error);
    return false;
  }
}

export const deleteLead = async (id: string): Promise<boolean> => {
  try {
    // Get a reference to the lead document
    const leadRef = doc(db, COLLECTION_NAME, id);

    // Get the document to check if it exists
    const leadDoc = await getDoc(leadRef);

    if (!leadDoc.exists()) {
      console.log("Lead document does not exist, nothing to delete");
      return true;
    }

    // Delete the document
    await deleteDoc(leadRef);

    // Also delete any subcollections (like history) if they exist
    // Note: This is a recursive delete that will remove all subcollections
    const batch = writeBatch(db);
    const collections = ['history'];

    for (const collectionName of collections) {
      const subcollectionRef = collection(leadRef, collectionName);
      const subcollectionDocs = await getDocs(subcollectionRef);

      subcollectionDocs.forEach((doc) => {
        batch.delete(doc.ref);
      });
    }

    await batch.commit();

    // Invalidate cache for this tenant since lead was deleted
    cache.invalidatePattern('^leads:');

    return true;
  } catch (error) {
    console.error("Error deleting lead:", error);
    return false;
  }
};

export async function assignLead(leadId: string, userId: string): Promise<boolean> {
  try {
    const leadRef = doc(db, "leads", leadId);
    const leadDoc = await getDoc(leadRef);

    if (!leadDoc.exists()) {
      console.error("Lead not found:", leadId);
      return false;
    }

    await updateDoc(leadRef, {
      assignedTo: userId,
      status: "assigned",
      assignedAt: new Date().toISOString()
    });

    // Invalidate cache for this tenant since assignment was changed
    cache.invalidatePattern('^leads:');

    return true;
  } catch (error) {
    console.error("Error assigning lead:", error);
    return false;
  }
}

// Special function to update ONLY notes without triggering full lead update
export async function updateLeadNotes(
  leadId: string,
  mutation: NoteMutation,
  historyEntry?: HistoryEntry
): Promise<boolean> {
  try {
    const leadRef = doc(db, COLLECTION_NAME, leadId);

    await runTransaction(db, async transaction => {
      const leadDoc = await transaction.get(leadRef);
      if (!leadDoc.exists()) throw new Error('Lead not found for notes update');
      const data = leadDoc.data();
      const notes = getLeadNotes({ ...data, id: leadId });
      const notesHistory = applyNoteMutation(notes, mutation);
      // An identical add is already persisted; do not duplicate its audit entry.
      if (notesHistory === notes) return;
      transaction.update(leadRef, {
        notesHistory,
        // Prevent a deleted migrated note reappearing through the legacy fallback.
        notes: '',
        ...(historyEntry ? { history: arrayUnion(historyEntry) } : {}),
      });
    });

    // Invalidate cache for this tenant since notes were updated
    cache.invalidatePattern('^leads:');

    logger.log("Successfully updated notes for lead:", leadId);
    return true;
  } catch (error) {
    logger.error("Error updating lead notes:", error);
    return false;
  }
}

export async function transferLead(leadId: string, adminId: string): Promise<boolean> {
  try {
    const leadRef = doc(db, "leads", leadId);
    const leadDoc = await getDoc(leadRef);

    if (!leadDoc.exists()) {
      console.error("Lead not found:", leadId);
      return false;
    }

    const leadData = leadDoc.data();
    const historyEntry: HistoryEntry = {
      date: new Date().toISOString(),
      type: "updated",
      changes: [
        {
          field: "assignedTo",
          oldValue: leadData.assignedTo,
          newValue: adminId
        }
      ],
      user: adminId
    };

    await updateDoc(leadRef, {
      assignedTo: adminId,
      assignedAt: new Date().toISOString(),
      transferredAt: new Date().toISOString(),
      history: [...(leadData.history || []), historyEntry]
    });

    // Invalidate cache for this tenant since lead was transferred
    cache.invalidatePattern('^leads:');

    return true;
  } catch (error) {
    console.error("Error transferring lead:", error);
    return false;
  }
}

/**
 * Fetch leads for a user filtered by folder
 * @param userId - The user ID
 * @param folderId - The folder ID (null for all leads, "__NO_FOLDER__" for unassigned)
 * @param tenantId - The tenant ID
 */
export const fetchUserLeadsByFolder = async (
  userId: string,
  folderId: string | null,
  tenantId: string = LEGACY_TENANT_ID
): Promise<Lead[]> => {
  try {
    const leadsRef = collection(db, COLLECTION_NAME);

    if (folderId === null) {
      // All leads for user (no folder filter)
      const q = query(
        leadsRef,
        where("tenantId", "==", tenantId),
        where("assignedTo", "==", userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertFirestoreData(doc));
    } else if (folderId === "__NO_FOLDER__") {
      // Only leads without folder assignment
      // Firestore can't query for null, so we query all and filter client-side
      const q = query(
        leadsRef,
        where("tenantId", "==", tenantId),
        where("assignedTo", "==", userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(doc => convertFirestoreData(doc))
        .filter(lead => !lead.folderId);
    } else {
      // Specific folder
      const q = query(
        leadsRef,
        where("tenantId", "==", tenantId),
        where("assignedTo", "==", userId),
        where("folderId", "==", folderId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertFirestoreData(doc));
    }
  } catch (error) {
    logger.error("Error fetching user leads by folder:", error);
    return [];
  }
};

/**
 * Update the folder assignment for a single lead
 */
export const updateLeadFolder = async (
  leadId: string,
  folderId: string | null
): Promise<boolean> => {
  try {
    const leadRef = doc(db, COLLECTION_NAME, leadId);
    await updateDoc(leadRef, {
      folderId: folderId,
      lastUpdated: new Date().toISOString()
    });

    // Invalidate cache for this tenant since folder was changed
    cache.invalidatePattern('^leads:');

    return true;
  } catch (error) {
    logger.error("Error updating lead folder:", error);
    return false;
  }
};

/**
 * Batch update folder assignment for multiple leads
 */
export const batchUpdateLeadsFolders = async (
  leadIds: string[],
  folderId: string | null
): Promise<boolean> => {
  try {
    if (leadIds.length === 0) return true;

    const batch = writeBatch(db);
    const now = new Date().toISOString();

    leadIds.forEach(leadId => {
      const leadRef = doc(db, COLLECTION_NAME, leadId);
      batch.update(leadRef, {
        folderId: folderId,
        lastUpdated: now
      });
    });

    await batch.commit();

    // Invalidate cache for this tenant since folders were changed
    cache.invalidatePattern('^leads:');

    logger.log(`Updated folder for ${leadIds.length} leads`);
    return true;
  } catch (error) {
    logger.error("Error batch updating lead folders:", error);
    return false;
  }
}
